import { useClientStore } from '@/stores/clientStore';
import ApiClient from '@/services/apiClient';
import Delay from "@/models/delay";
import RemoteOutput from './remoteOutput';
import i18n from '@/i18n';

const t = (key) => i18n.global.t(key);

export default class Action {
    id;
    is_active;
    action_type;
    control_doors;
    control_outputs;
    control_remote_outputs;
    send_email;

    constructor(id = 0) {
        this.id = id;
        this.action_type = "";
        this.is_active = true;
        this.control_doors = new ActionControlDoors();
        this.control_outputs = new ActionControlOutputs();
        this.control_remote_outputs = new ActionControlRemoteOutputs();
        this.send_email = new ActionSendEmail();
    }

    static constructFromObject(data, obj) {
        if (data) {
            obj = obj || new Action();
            obj.id = ApiClient.convertToType(data['id'], 'Integer');
            obj.is_active = ApiClient.convertToType(data['is_active'], 'Boolean');
            obj.action_type = ApiClient.convertToType(data['action_type'], 'String');
            switch(obj.action_type) {
                case "control_doors":
                    obj.control_doors = ApiClient.convertToType(data['control_doors'], ActionControlDoors);
                    break;
                case "control_outputs":
                    obj.control_outputs = ApiClient.convertToType(data['control_outputs'], ActionControlOutputs);
                    break;
                case "control_remote_outputs":
                    obj.control_remote_outputs = ApiClient.convertToType(data['control_remote_outputs'], ActionControlRemoteOutputs);
                    break;
                case "send_email":
                    obj.send_email = ApiClient.convertToType(data['send_email'], ActionSendEmail);
                    break;
            }
        }
        return obj;
    }

    toJSON() {
        const json = {
            id: this.id,
            is_active: this.is_active,
            action_type: this.action_type,
        };
        switch(this.action_type) {
            case "control_doors":
                json.control_doors = this.control_doors;
                break;
            case "control_outputs":
                json.control_outputs = this.control_outputs;
                break;
            case "control_remote_outputs":
                json.control_remote_outputs = this.control_remote_outputs;
                break;
            case "send_email":
                json.send_email = this.send_email;
                break;
        }

        return json;
    }

    toString() {
        switch(this.action_type) {
            case "control_doors":
                return this.control_doors.toString();
            case "control_outputs":
                return this.control_outputs.toString();
            case "control_remote_outputs":
                return this.control_remote_outputs.toString();
            case "send_email":
                return this.send_email.toString();
            default:
                return t('action.unknown');
        }
    }
}

export class ActionControlDoors {
    doors;
    mode;       //Open, ForcedOpen, Close, ForcedClose, StopForcedMode

    constructor() {
        this.doors = [];
        this.mode = 'Open';
    }

    static constructFromObject(data, obj) {
        obj = obj || new ActionControlDoors();
        if (data) {
            obj.doors = ApiClient.convertToType(data['doors'], ['Integer']);
            obj.mode = ApiClient.convertToType(data['mode'], 'String');
        }
        return obj;
    }

    toString() {
        const clientStore = useClientStore();
        let text = '';
        switch(this.mode) {
            case 'Open':
                text = t('action.open') + ' ';
                break;
            case 'ForcedOpen':
                text = t('action.forcedOpen') + ' ';
                break;
            case 'Close':
                text = t('action.close') + ' ';
                break;
            case 'ForcedClose':
                text = t('action.forcedClose') + ' ';
                break;
            case 'StopForcedMode':
                text = t('action.stopForcedMode') + ' ';
                break;
        }
        text += `<b>${clientStore.getDoors(this.doors).join(", ")}</b>`;
        return text;
    }
}

export class ActionControlOutputs{
    outputs;
    mode;
    delay;

    constructor() {
        this.outputs = [];
        this.mode = 'Activate';
        this.delay = new Delay(1);
    }

    static constructFromObject(data, obj) {
        obj = obj || new ActionControlOutputs();
        if (data) {
            obj.outputs = ApiClient.convertToType(data['outputs'], ['Integer']);
            obj.mode = ApiClient.convertToType(data['mode'], 'String');
            obj.delay = ApiClient.convertToType(data['delay'], Delay) || new Delay(1);
        }
        return obj;
    }

    toJSON() {
        const json = {
            outputs: this.outputs,
            mode: this.mode,
        };
        if(this.mode == 'ActiveFor') {
            json.delay = this.delay;
        }
        return json;
    }

    toString() {
        const clientStore = useClientStore();
        let text = '';
        switch(this.mode) {
            case 'Activate':
                text += t('action.activate') + ' ';
                break;
            case 'ActiveFor':
                text += t('action.activateFor') + ' ' + this.getDelay();
                break;
            case 'Deactivate':
                text += t('action.deactivate') + ' ';
                break;
            case 'Invert':
                text += t('action.invert') + ' ';
                break;
        }
        text += (this.outputs.length == 1 ? t('action.output') : t('action.outputs')) + ' ';
        text += `<b>${clientStore.getOutputs(this.outputs).join(", ")}</b>`;
        return text;
    }

    getDelay() {
        return `${this.delay.value}${this.delay.unit} `;
    }
}

export class ActionControlRemoteOutputs{
    remote_outputs;
    mode;
    delay;

    constructor() {
        this.remote_outputs = [];
        this.mode = 'Activate';
        this.delay = new Delay(1);
    }

    static constructFromObject(data, obj) {
        obj = obj || new ActionControlRemoteOutputs();
        if (data) {
            obj.remote_outputs = ApiClient.convertToType(data['remote_outputs'], [RemoteOutput]);
            obj.mode = ApiClient.convertToType(data['mode'], 'String');
            obj.delay = ApiClient.convertToType(data['delay'], Delay) || new Delay(1);
        }
        return obj;
    }

    toJSON() {
        const json = {
            remote_outputs: this.remote_outputs,
            mode: this.mode,
        };
        if(this.mode == 'ActiveFor') {
            json.delay = this.delay;
        }
        return json;
    }

    toString() {
        const clientStore = useClientStore();
        let text = '';
        switch(this.mode) {
            case 'Activate':
                text += t('action.activate') + ' ';
                break;
            case 'ActiveFor':
                text += t('action.activateFor') + ' ' + this.getDelay();
                break;
            case 'Deactivate':
                text += t('action.deactivate') + ' ';
                break;
            case 'Invert':
                text += t('action.invert') + ' ';
                break;
        }
        text += (this.remote_outputs.length == 1 ? t('action.output') : t('action.outputs')) + ' ';
        text += `<b>${clientStore.getRemoteOutputs(this.remote_outputs).join(", ")}</b>`;
        return text;
    }

    getDelay() {
        return `${this.delay.value}${this.delay.unit} `;
    }
}

export class ActionSendEmail {
    recipients;
    subject;
    body;

    constructor() {
        this.recipients = [];
        this.subject = "";
        this.body = "";
    }

    static constructFromObject(data, obj) {
        obj = obj || new ActionSendEmail();
        if (data) {
            obj.recipients = ApiClient.convertToType(data['recipients'], ['String']);
            obj.subject = ApiClient.convertToType(data['subject'], 'String');
            obj.body = ApiClient.convertToType(data['body'], 'String');
        }
        return obj;
    }

    toString() {
        return `${t('action.sendEmail')} <b>"${this.subject}"</b> ${t('action.emailTo')} <b>${this.recipients.join(', ')}</b>`;
    }
}
